import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import type { ITimelineItem, ITimelineRequestBody } from '~/types/types';
import { getTimelineData } from '~/utils/dataProviders/TimelineApiDataProvider';
import { AbortError } from '~/utils/errors/AbortError';
import moment, { type MomentInput } from 'moment';
import { useUrlParams } from '~/composables/urlParams';

export const useEventCalendarStore = defineStore('eventCalendar', () => {
    const items = ref<ITimelineItem[]>([]);
    const validFrom = ref('');
    const validTo = ref('');
    const postGroupIds = ref<number[]>([]);
    const isLoading = ref(false);
    const abortController = ref(new AbortController());

    const setRange = (from: MomentInput, to: MomentInput) => {
        validFrom.value = moment(from).format('YYYY-MM-DD 00:00:00');
        validTo.value = moment(to).format('YYYY-MM-DD 23:59:59');
    };

    const setPostGroupIds = (groupIds: number[]) => {
        postGroupIds.value = groupIds || [];
    };

    const fetchEventCalendar = async (locale: string, body: ITimelineRequestBody) => {
        abortController.value.abort();
        abortController.value = new AbortController();
        isLoading.value = true;
        try {
            const result =  await getTimelineData(locale, body, abortController.value.signal);
            isLoading.value = false;
            return result ?? [];
        } catch (error) {
            if (error instanceof AbortError) {
                return items.value;
            }
            throw error;
        }
    };

    const { addParam } = useUrlParams();
    watch([validFrom, validTo],  async ([newFrom, newTo]) => {
        if (!newFrom || !newTo) {
            return;
        }
        await addParam('validFrom', moment(newFrom).format('YYYY-MM-DD'));
        await addParam('validTo', moment(newTo).format('YYYY-MM-DD'));
    });

    return { items, validFrom, validTo, postGroupIds, isLoading, setRange, setPostGroupIds, fetchEventCalendar };
});
